import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBlockHighlight } from '@ContractBuilder/utils/use-block-highlight';
import {
  clearSearchHighlights,
  getHighlightBlockRoots,
  getSearchHighlightMarks,
  highlightSearchTerms,
  setActiveSearchHighlight,
} from '@utils/highlight-search-terms';

const HIGHLIGHT_PARAM = 'highlight';
const BLOCK_IDS_PARAM = 'blockIds';

/** Whether a mutation landed inside one of the highlighted blocks, or mounted/unmounted one of them. */
const touchesHighlightedBlock = (records: MutationRecord[], blockIds: string[]): boolean => {
  const blockRoots = getHighlightBlockRoots(document, blockIds);
  const blockSelectors = blockIds.map((blockId) => `[id="${blockId}"]`);

  const isRelevantNode = (node: Node) => {
    const element = node instanceof Element ? node : node.parentElement;
    if (element === null) {
      return false;
    }
    return (
      blockRoots.some((blockRoot) => blockRoot.contains(element)) ||
      blockSelectors.some((selector) => element.matches(selector) || element.querySelector(selector) !== null)
    );
  };

  return records.some(
    (record) =>
      isRelevantNode(record.target) ||
      [...record.addedNodes, ...record.removedNodes].some((node) => isRelevantNode(node)),
  );
};

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.closest('button, a, [role=button]') !== null
  );
};

/**
 * Reads the `?highlight=` query param (set by command palette navigation), marks the matching text inside the
 * rendered block content, and keeps re-applying it as blocks re-render (e.g. once mention/variation values resolve
 * and reset the block's innerHTML). Exposes find-in-document style stepping through the matches.
 */
export const useSearchHighlightNav = () => {
  const location = useLocation();
  const [matchCount, setMatchCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);

  const navigate = useNavigate();

  const { onClick: onJumpToTarget } = useBlockHighlight();

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    setActiveIndex(0);
    activeIndexRef.current = 0;

    const searchParams = new URLSearchParams(location.search);
    const terms = searchParams.get(HIGHLIGHT_PARAM)?.split('|') ?? [];
    const blockIds = searchParams.get(BLOCK_IDS_PARAM)?.split(',') ?? [];

    if (terms.length === 0) {
      setMatchCount(0);
      return;
    }

    // Blocks re-render after their mentions/variations resolve, which resets their innerHTML and destroys our
    // marks. Re-apply on every relevant mutation, pausing the observer while we mutate the DOM ourselves.
    const observer = new MutationObserver((records) => {
      if (touchesHighlightedBlock(records, blockIds)) {
        applyHighlight({ scroll: false });
      }
    });

    const observe = () => {
      observer.takeRecords();
      observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    };

    const applyHighlight = ({ scroll }: { scroll: boolean }) => {
      observer.disconnect();

      clearSearchHighlights(document);
      highlightSearchTerms(document, terms, blockIds);

      const marks = getSearchHighlightMarks(document);
      const clampedIndex = marks.length === 0 ? 0 : Math.min(activeIndexRef.current, marks.length - 1);

      setMatchCount(marks.length);
      setActiveIndex(clampedIndex);
      activeIndexRef.current = clampedIndex;
      setActiveSearchHighlight(document, clampedIndex, onJumpToTarget, { scroll });

      observe();
    };

    applyHighlight({ scroll: true });

    return () => {
      observer.disconnect();
      clearSearchHighlights(document);
    };
  }, [location.search, onJumpToTarget]);

  const stepTo = useCallback(
    (nextIndex: number) => {
      setActiveIndex(nextIndex);
      setActiveSearchHighlight(document, nextIndex, onJumpToTarget);
    },
    [onJumpToTarget],
  );

  const goToNext = useCallback(() => {
    if (matchCount === 0) {
      return;
    }
    stepTo((activeIndexRef.current + 1) % matchCount);
  }, [matchCount, stepTo]);

  const goToPrevious = useCallback(() => {
    if (matchCount === 0) {
      return;
    }
    stepTo((activeIndexRef.current - 1 + matchCount) % matchCount);
  }, [matchCount, stepTo]);

  const dismiss = useCallback(() => {
    // remove highlights from query string
    clearSearchHighlights(document);
    navigate(location.pathname);
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (matchCount === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || isEditableTarget(event.target)) {
        return;
      }

      // bail out when the event target is an interactive element (closest('button, a, [role=button]'))

      event.preventDefault();
      if (event.shiftKey) {
        goToPrevious();
      } else {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [matchCount, goToNext, goToPrevious]);

  return {
    matchCount,
    activeIndex,
    isVisible: matchCount > 0,
    goToNext,
    goToPrevious,
    dismiss,
  };
};
